import { useLoaderData, useNavigate } from "react-router-dom";
import { AviTracerApi } from "../../../avitracerApi";
import { useAppDispatch } from "../../../store/hooks";
import BaseFplPage, { BaseIfrSelectResult } from "../BaseFplPage";
import FlightPlan from "../../../models/flightPlan";
import { isIfrFlightRules } from "../../../models/flight";
import { IfrPlanningOverrides, IfrRouteResult } from "../../../avitracerApi";
import { cancelFlightPlanFpl, delayFlightPlanFpl, fileFlightPlanFpl, upsertFlightPlan } from "../../../store/flightPlansSlice";

function FlightPlanFplPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const loadedFlightPlan = useLoaderData() as FlightPlan;

  const getPdfDoc = async () => {
    const pdfData = await AviTracerApi.getFlightPlanPdfDoc(loadedFlightPlan.id!, "icaoFpl");
    return pdfData;
  };

  //Errors are NOT caught here — they propagate to BaseFplPage, which shows them inline and keeps the
  //user on the page (no js alerts in this project).
  const fileFplUsingAviTracer = async () => {
    await dispatch(fileFlightPlanFpl(loadedFlightPlan.id!)).unwrap();
  };

  const refileFpl = async () => {
    await AviTracerApi.refileFlightPlanFpl(loadedFlightPlan.id!);
  };

  const chgFpl = async (f15: string, f18: string) => {
    await AviTracerApi.chgFlightPlanFpl(loadedFlightPlan.id!, f15, f18);
  }

  const cancelFiledFpl = async () => {
    await dispatch(cancelFlightPlanFpl(loadedFlightPlan.id!)).unwrap();
  };

  const delayFiledFpl = async (newTime: Date) => {
    await dispatch(delayFlightPlanFpl({ flightPlanId: loadedFlightPlan.id!, newTime: newTime })).unwrap();
  };

  const checkManualRoute = async (field15: string, overrides?: IfrPlanningOverrides): Promise<IfrRouteResult> => {
    const resp = await AviTracerApi.checkFlightPlanIfrRoute(loadedFlightPlan.id!, field15, overrides);
    return resp.result;
  };

  const ifrPlanningDefaults = {
    departure: loadedFlightPlan.departure?.isIcao ? loadedFlightPlan.departure.icaoCode : undefined,
    destination: loadedFlightPlan.destination?.isIcao ? loadedFlightPlan.destination.icaoCode : undefined,
    alternate1: loadedFlightPlan.altn1,
    alternate2: loadedFlightPlan.altn2,
  };

  const findIfrRoutes = async (optimizes: string[], levels?: number[], overrides?: IfrPlanningOverrides): Promise<IfrRouteResult[]> => {
    const resp = await AviTracerApi.findFlightPlanIfrRoutes(loadedFlightPlan.id!, optimizes, levels, overrides);
    return resp.results;
  };

  const selectIfrRoute = async (field15: string, overrides?: IfrPlanningOverrides): Promise<BaseIfrSelectResult> => {
    const resp = await AviTracerApi.selectFlightPlanIfrRoute(loadedFlightPlan.id!, field15, overrides);
    dispatch(upsertFlightPlan(resp.flightPlan));
    return { fplFile: resp.flightPlan.fplFile, ifpsRoute: resp.flightPlan.ifpsRoute };
  };

  const validateFpl = async () => {
    const resp = await AviTracerApi.validateFlightPlanFpl(loadedFlightPlan.id!);
    //The backend persisted this result on the plan — mirror it into Redux so the plans list badge reflects
    //it too (otherwise the list keeps showing the pre-validation "Ready to validate" state).
    dispatch(
      upsertFlightPlan({
        ...loadedFlightPlan,
        ifrValidation: {
          result: resp.validation.result,
          validation: resp.validation.validation,
          errors: resp.validation.errors,
          fplCode: resp.fplCode,
          at: new Date().toISOString(),
        },
      })
    );
    return resp;
  };

  const applyProposedRoute = async (route: string) => {
    const resp = await AviTracerApi.applyFlightPlanProposedRoute(loadedFlightPlan.id!, route);
    dispatch(upsertFlightPlan(resp));
    return resp;
  };

  const goBack = () => {
    navigate("/fpls");
  };

  return (
    <BaseFplPage
      data={{
        fplFile: loadedFlightPlan.fplFile,
        flightPlanPicDetails: loadedFlightPlan.picFplDetails,
        departureDateTime: new Date(loadedFlightPlan.departureDateTime),
        flightId: loadedFlightPlan.flightId ?? loadedFlightPlan.aircraftRegistration,
        isIfr: isIfrFlightRules(loadedFlightPlan.flightRules),
        ifpsRoute: loadedFlightPlan.ifpsRoute,
        altn1: loadedFlightPlan.altn1,
        //Straight off the plan — no aircraft fallback, same reasoning as flights.
        ifrFuelPerHour: loadedFlightPlan.ifrFuelPerHour,
        ifrMaxMass: loadedFlightPlan.ifrMaxMass,
        ifrValidation: loadedFlightPlan.ifrValidation,
      }}
      getPdfDoc={getPdfDoc}
      fileFplUsingAviTracerAction={fileFplUsingAviTracer}
      refileFplAction={refileFpl}
      chgFplAction={chgFpl}
      cancelFiledFplAction={cancelFiledFpl}
      delayFiledFplAction={delayFiledFpl}
      findIfrRoutesAction={findIfrRoutes}
      checkManualRouteAction={checkManualRoute}
      ifrPlanningDefaults={ifrPlanningDefaults}
      selectIfrRouteAction={selectIfrRoute}
      validateFplAction={validateFpl}
      applyProposedRouteAction={applyProposedRoute}
      goBack={goBack}
    />
  );
}

export default FlightPlanFplPage;
